import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ChevronDown, Phone, MapPin, Landmark, Calculator, Clock } from "lucide-react";
import { usePageMeta } from "@/components/Layout";
import { BIZ, FAQS } from "@/data/site";
import { bySide, COVERAGE } from "@/data/coverage";
import { useLang } from "@/i18n/LanguageContext";
import { useJsonLd, faqSchema } from "@/components/Seo";

const HEADLINE_LINES = ["Insurance that someone", "actually read before", "you signed it."];

const Hero = () => {
  const reduced = useReducedMotion();
  const { lang, t } = useLang();
  return (
    <section className="border-b border-[var(--line)] bg-white">
      <div className="wrap grid gap-12 py-16 sm:py-20 lg:grid-cols-[7fr_5fr] lg:items-center lg:py-24">
        <div>
          <h1 className="text-4xl leading-[1.12] sm:text-5xl lg:text-[50px] xl:text-[56px]" data-testid="hero-headline">
            {HEADLINE_LINES.map((line, i) => (
              <span key={`${lang}-${i}`} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={reduced ? false : { y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {t(line)}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            className="mt-6 max-w-[58ch] text-lg leading-relaxed"
            initial={reduced ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
          >
            {t("We're an independent agency in Frisco. We quote your risk with several carriers, explain the differences in plain English, and stay on the phone when a claim goes sideways.")}
          </motion.p>
          <motion.div
            className="mt-8 flex flex-wrap gap-3"
            initial={reduced ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
          >
            <Link to="/quote" className="btn btn-navy" data-testid="hero-quote-button">
              {t("Get my quote")} <ArrowRight size={16} strokeWidth={1.75} />
            </Link>
            <Link to="/policy-review" className="btn btn-quiet" data-testid="hero-review-button">
              {t("Free policy review")}
            </Link>
          </motion.div>
        </div>

        <motion.div
          className="hidden lg:block"
          initial={reduced ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
          aria-hidden="true"
        >
          <div className="border border-[var(--line)] bg-[var(--paper)] p-7">
            <p className="font-display text-lg text-[var(--ink)]">{t("What we check before you sign")}</p>
            <dl className="mt-4">
              {[
                ["Wind & hail deductible", "Percentage of building value, not a flat number — we do the math out loud"],
                ["Roof settlement", "Replacement cost or actual cash value — the difference is the whole claim"],
                ["Flood zone", "Collin County creek zones your lender never mentioned"],
                ["Contract wording", "Additional insured, primary and non-contributory, waiver of subrogation"],
              ].map(([k, v]) => (
                <div key={k} className="grid grid-cols-[150px_1fr] gap-4 border-t border-[var(--line)] py-3 text-[13.5px]">
                  <dt className="font-semibold text-[var(--ink)]">{t(k)}</dt>
                  <dd className="m-0 text-[var(--ink-soft)]">{t(v)}</dd>
                </div>
              ))}
            </dl>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const Fork = () => {
  const biz = bySide("business");
  const per = bySide("personal");
  const { t } = useLang();
  return (
    <section className="wrap py-14 sm:py-20">
      <div className="grid gap-5 md:grid-cols-2">
        <Link
          to="/business"
          data-testid="fork-business-panel"
          className="group block bg-[var(--ink)] p-8 text-white transition-shadow duration-200 hover:shadow-[0_14px_40px_rgba(6,36,77,0.28)] sm:p-10"
        >
          <h2 className="font-display text-2xl !text-white sm:text-3xl">{t("Business insurance")}</h2>
          <p className="mt-2 max-w-[46ch] text-[15px] text-white/70">
            {t("For North Texas businesses — from the lease requirement to the audit letter.")}
          </p>
          <ul className="mt-6 space-y-2.5">
            {biz.slice(0, 4).map((c) => (
              <li key={c.slug} className="flex items-center gap-2.5 text-[15px] text-white/85">
                <span className="h-[2px] w-3 shrink-0 bg-[var(--accent)]" /> {t(c.name)}
              </li>
            ))}
          </ul>
          <span className="mt-8 inline-flex items-center gap-2 text-[15px] font-semibold text-white">
            {t("See business coverage")} <ArrowRight size={16} strokeWidth={1.75} className="transition-transform group-hover:translate-x-1" />
          </span>
        </Link>

        <Link
          to="/personal"
          data-testid="fork-personal-panel"
          className="group block bg-[var(--tint)] p-8 transition-shadow duration-200 hover:shadow-[0_14px_40px_rgba(6,36,77,0.14)] sm:p-10"
        >
          <h2 className="font-display text-2xl sm:text-3xl">{t("Personal insurance")}</h2>
          <p className="mt-2 max-w-[46ch] text-[15px]">
            {t("Home, auto, and the umbrella that protects everything above them.")}
          </p>
          <ul className="mt-6 space-y-2.5">
            {per.map((c) => (
              <li key={c.slug} className="flex items-center gap-2.5 text-[15px] text-[var(--ink)]">
                <span className="h-[2px] w-3 shrink-0 bg-[var(--accent-deep)]" /> {t(c.name)}
              </li>
            ))}
          </ul>
          <span className="mt-8 inline-flex items-center gap-2 text-[15px] font-semibold text-[var(--ink)]">
            {t("See personal coverage")} <ArrowRight size={16} strokeWidth={1.75} className="transition-transform group-hover:translate-x-1" />
          </span>
        </Link>
      </div>

      <Link
        to="/specialty"
        data-testid="fork-specialty-strip"
        className="mt-5 flex flex-col gap-2 border border-[var(--line)] bg-white px-6 py-5 transition-colors hover:border-[var(--ink)] sm:flex-row sm:items-center sm:justify-between"
      >
        <span className="text-[15px] font-semibold text-[var(--ink)]">{t("Something more specific?")}</span>
        <span className="text-[13.5px] text-[var(--ink-soft)]">
          {t("Flood · Bonds · Professional liability · D&O and EPLI · Events · Boats and RVs · Farm and ranch · Life · Health and Medicare")}
        </span>
        <ArrowRight size={16} strokeWidth={1.75} className="hidden shrink-0 text-[var(--ink)] sm:block" />
      </Link>
    </section>
  );
};

const TRUST = [
  { icon: Landmark, title: "Independent", text: "We represent several carriers and work for you, not for one of them." },
  { icon: Calculator, title: "CPA-led", text: "{agent} is a Texas CPA. Your coverage gets read the way your books do." },
  { icon: MapPin, title: "Local", text: "{address}" },
  { icon: Clock, title: "One business day", text: "That's how fast we follow up on every request that comes through this site." },
];

const TrustStrip = () => {
  const { t } = useLang();
  return (
  <section className="border-y border-[var(--line)] bg-white">
    <div className="wrap grid grid-cols-1 divide-y divide-[var(--line)] sm:grid-cols-2 sm:divide-y-0 lg:grid-cols-4 lg:divide-x">
      {TRUST.map((item) => (
        <div key={item.title} className="px-0 py-7 sm:px-6 lg:first:pl-0 lg:last:pr-0">
          <item.icon size={20} strokeWidth={1.5} className="text-[var(--accent-deep)]" />
          <h3 className="mt-3 font-sans text-[15px] font-semibold text-[var(--ink)]">{t(item.title)}</h3>
          <p className="mt-1.5 text-[13.5px] leading-relaxed">{t(item.text, { agent: BIZ.agent, address: BIZ.address })}</p>
        </div>
      ))}
    </div>
  </section>
  );
};

const Marquee = () => {
  const { t } = useLang();
  return (
  <div className="marquee bg-white py-3" aria-hidden="true">
    <div className="marquee-track">
      {[0, 1].map((n) => (
        <span key={n} className="flex shrink-0 items-center">
          {COVERAGE.map((c) => (
            <span key={c.slug} className="mx-6 flex items-center gap-6 font-display text-[15px] text-[var(--ink-soft)]">
              {t(c.name)} <span className="h-1 w-1 rounded-full bg-[var(--accent)]" />
            </span>
          ))}
        </span>
      ))}
    </div>
  </div>
  );
};

const STEPS = [
  ["Tell us what you have", "Five questions on this site, or one phone call. Upload a declarations page if you have one — it saves everyone twenty minutes."],
  ["We shop it", "We take your risk to the carriers we represent, compare what comes back, and read the exclusions before you do."],
  ["You choose, informed", "You get the options side by side, in plain English, with the trade-offs stated. Then you decide."],
];

const HowItWorks = () => {
  const { t } = useLang();
  return (
  <section className="wrap py-14 sm:py-20">
    <h2 className="text-2xl sm:text-3xl lg:text-4xl">{t("How a quote works")}</h2>
    <div className="mt-10 grid gap-10 md:grid-cols-3 md:gap-8">
      {STEPS.map(([title, text], i) => (
        <div key={title} className="border-t-2 border-[var(--accent)] pt-5">
          <span className="font-display text-4xl text-[var(--ink)]">{i + 1}</span>
          <h3 className="mt-3 font-sans text-[16px] font-semibold text-[var(--ink)]">{t(title)}</h3>
          <p className="mt-2 max-w-[44ch] text-[14.5px] leading-relaxed">{t(text)}</p>
        </div>
      ))}
    </div>
  </section>
  );
};

const ClaimsBand = () => {
  const { t } = useLang();
  return (
  <section className="bg-[var(--ink)]">
    <div className="wrap flex flex-col items-start justify-between gap-6 py-12 sm:py-14 md:flex-row md:items-center">
      <div>
        <h2 className="text-2xl text-white sm:text-3xl">{t("Had a loss?")}</h2>
        <p className="mt-2 max-w-[56ch] text-[15px] text-white/70">
          {t("Call us at")} <a href={BIZ.phoneHref} className="font-semibold text-white underline">{BIZ.phone}</a> {t("and we'll walk you through the next hour — what to photograph, who to call, and what not to sign yet.")}
        </p>
      </div>
      <div className="flex flex-wrap gap-3">
        <a href={BIZ.phoneHref} className="btn btn-white" data-testid="claims-band-call-button">
          <Phone size={16} strokeWidth={1.75} /> {BIZ.phone}
        </a>
        <Link to="/claims" className="btn btn-ghost-white" data-testid="claims-band-report-button">
          {t("Report a claim")}
        </Link>
      </div>
    </div>
  </section>
  );
};

const Faq = () => {
  const [open, setOpen] = useState(null);
  const { t } = useLang();
  return (
    <section className="wrap max-w-[860px] py-14 sm:py-20">
      <h2 className="text-2xl sm:text-3xl lg:text-4xl">{t("Questions we get a lot")}</h2>
      <div className="mt-8 border-t border-[var(--line)]">
        {FAQS.map((f, i) => (
          <div key={f.q} className="border-b border-[var(--line)]">
            <button
              type="button"
              aria-expanded={open === i}
              data-testid={`faq-question-${i}`}
              onClick={() => setOpen(open === i ? null : i)}
              className="flex w-full items-center justify-between gap-4 py-5 text-left text-[16px] font-medium text-[var(--ink)] transition-colors hover:text-[var(--blue)]"
            >
              {t(f.q)}
              <ChevronDown size={17} strokeWidth={1.75} className={`shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
            </button>
            {open === i && (
              <p className="max-w-[68ch] pb-6 text-[15px] leading-relaxed" data-testid={`faq-answer-${i}`}>{t(f.a)}</p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

const CITIES = ["Frisco", "Plano", "McKinney", "Allen", "Prosper", "Celina", "Little Elm", "The Colony"];

const Areas = () => {
  const { t } = useLang();
  return (
    <section className="border-t border-[var(--line)] bg-white">
      <div className="wrap py-14 sm:py-20">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl">{t("Serving Frisco and North Texas")}</h2>
        <p className="mt-4 max-w-[72ch] text-[15px] leading-relaxed">
          {t("We're an independent insurance agency based in Frisco, writing business and personal coverage across Collin and Denton counties and the wider Dallas–Fort Worth metroplex. Every policy is quoted with several carriers and handled by a licensed Texas agent — by phone, by email, or in person at our WestRidge office.")}
        </p>
        <ul className="mt-6 flex flex-wrap gap-2.5" data-testid="areas-city-list">
          {CITIES.map((city) => (
            <li key={city} className="border border-[var(--line)] bg-[var(--paper)] px-3.5 py-1.5 text-[13px] font-semibold text-[var(--ink)]">{city}, TX</li>
          ))}
          <li className="border border-[var(--line)] bg-[var(--paper)] px-3.5 py-1.5 text-[13px] font-semibold text-[var(--ink)]">{t("Dallas–Fort Worth metroplex")}</li>
        </ul>
      </div>
    </section>
  );
};

export default function Home() {
  usePageMeta(
    "Business & Personal Insurance in Frisco, TX | Artha Insurance",
    "Independent property and casualty agency in Frisco, Texas serving Plano, McKinney, Allen, Prosper, and the Dallas–Fort Worth area. Business and personal insurance quoted with several carriers and explained in plain English."
  );
  const { t } = useLang();
  useJsonLd("jsonld-faq-home", faqSchema(FAQS.map((f) => [t(f.q), t(f.a)])));
  return (
    <>
      <Hero />
      <Marquee />
      <Fork />
      <TrustStrip />
      <HowItWorks />
      <ClaimsBand />
      <Areas />
      <Faq />
    </>
  );
}
